import { Component, type ReactNode, type ErrorInfo } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "@/lib/icons";
import { captureException } from "@/lib/sentry";

interface ErrorLogEntry {
  timestamp: string;
  message: string;
  stack?: string;
  context?: Record<string, any>;
  userAgent: string;
  url: string;
}

const errorLog: ErrorLogEntry[] = [];
const MAX_ERROR_LOG_SIZE = 50;
const ERROR_THROTTLE_MS = 1000;
const MAX_ERRORS_PER_MINUTE = 10;

let lastErrorTime = 0;
let errorsThisMinute = 0;
let minuteResetTimeout: ReturnType<typeof setTimeout> | null = null;

function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + "...";
}

export function logError(error: Error, context?: Record<string, any>): void {
  if (typeof window === "undefined") return;

  const now = Date.now();
  
  if (now - lastErrorTime < ERROR_THROTTLE_MS) {
    return;
  }
  
  if (errorsThisMinute >= MAX_ERRORS_PER_MINUTE) {
    return;
  }
  
  lastErrorTime = now;
  errorsThisMinute++;
  
  if (!minuteResetTimeout) {
    minuteResetTimeout = setTimeout(() => {
      errorsThisMinute = 0;
      minuteResetTimeout = null;
    }, 60000);
  }

  const entry: ErrorLogEntry = {
    timestamp: new Date().toISOString(),
    message: truncateString(error.message || "Unknown error", 500),
    stack: error.stack ? truncateString(error.stack, 2000) : undefined,
    context: context ? {
      ...context,
      type: truncateString(String(context.type || "unknown"), 50),
    } : undefined,
    userAgent: truncateString(navigator.userAgent, 200),
    url: truncateString(window.location.href, 500),
  };

  if (errorLog.length >= MAX_ERROR_LOG_SIZE) {
    errorLog.shift();
  }
  errorLog.push(entry);

  console.error("[Error Monitor]", entry.message, entry.context?.type || "");

  // Send to Sentry
  captureException(error, context);

  if (process.env.NODE_ENV === "production") {
    sendErrorToServer(entry).catch(() => {});
  }
}

async function sendErrorToServer(entry: ErrorLogEntry): Promise<void> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    await fetch("/api/errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entry),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
  } catch {
  }
}

export function getErrorLog(): ErrorLogEntry[] {
  return [...errorLog];
}

export function clearErrorLog(): void {
  errorLog.length = 0;
}

export function setupGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    logError(event.error || new Error(event.message || "Unknown error"), {
      type: "window_error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason || "Unknown rejection"));
    
    logError(error, {
      type: "unhandled_rejection",
    });
  });
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    logError(error, {
      componentStack: errorInfo.componentStack?.substring(0, 1000),
      type: "react_error_boundary",
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen min-h-dvh flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-display font-bold">
                Algo salió mal
              </h1>
              <p className="text-muted-foreground">
                Ha ocurrido un error inesperado. Por favor, intenta recargar la página.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={this.handleGoHome} 
                variant="outline"
                data-testid="button-go-home"
              >
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Button>
              <Button 
                onClick={this.handleRetry}
                data-testid="button-retry"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reintentar
              </Button>
            </div>
            {(process.env.NODE_ENV === "development" || this.props.showDetails) && this.state.error && (
              <details className="mt-4 text-left p-4 bg-muted rounded-lg text-sm">
                <summary className="cursor-pointer font-medium">
                  Detalles del error
                </summary>
                <pre className="mt-2 overflow-auto text-xs text-destructive whitespace-pre-wrap">
                  {this.state.error.message}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
): React.FC<P> {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
